// src/context/LocaleProvider.tsx
import React, { createContext, useEffect, useState } from 'react';
import i18n from './i18n';
import { useDebounce, useLocalStorage } from '@uidotdev/usehooks';
import Api from 'axiosApi/api';
import { useApi } from 'api/ApiProvider';
import Spinner from 'components/Spinner';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { enUS, es } from 'date-fns/locale';
import { LOCALES_ISO_639_1 } from './locales';
import { GLOBAL_DATE_CONFIG } from 'common/constants';

const LOCALE_STORAGE_KEY = 'locale';

const DateFnsLocales: { [key in LOCALES_ISO_639_1]?: any } = {
  en: enUS,
  es: es,
};

export const LocaleContext = createContext({
  locale: 'en' as LOCALES_ISO_639_1,
  languagesAvailable: [] as LOCALES_ISO_639_1[],
  changeLocale: (locale: LOCALES_ISO_639_1) => {},
  reloadLocales: () => {},
  loading: true,
  error: false,
});

export default function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = useLocalStorage<LOCALES_ISO_639_1>(LOCALE_STORAGE_KEY, 'en');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const api: Api = useApi();
  const localeApi = api?.localeApi || null;
  const [languagesAvailable, setLanguagesAvailable] = useState<LOCALES_ISO_639_1[]>([]);
  const [resources, setResources] = useState<any>(null);

  const changeLocale = async (newLocale: LOCALES_ISO_639_1) => {
    setLocale(newLocale);
    await i18n.changeLanguage(newLocale);
  };

  const reloadLocales = async () => {
    setLoading(true);
    try {
      const response = await localeApi?.apiVversionLocaleGet('1');
      if (response.data.errors) throw new Error('Failed to fetch locales');
      const { data } = response.data;
      const res = {};

      for (const [lang, translations] of Object.entries(data)) {
        res[lang] = { translation: translations };
      }

      setResources(res);
      setLanguagesAvailable(Object.keys(res) as LOCALES_ISO_639_1[]);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localeApi) return;
    reloadLocales();
  }, [localeApi]);

  useEffect(() => {
    if (!resources) return;

    Object.entries(resources).forEach(([lang, data]: any) => {
      i18n.addResourceBundle(lang, 'translation', data.translation, true, true);
    });

    i18n.changeLanguage(locale);
  }, [resources, locale]);

  const debouncedLoading = useDebounce(loading || !resources, 200);

  return (
    <LocaleContext.Provider
      key={`lang-${locale}`}
      value={{
        locale,
        languagesAvailable,
        changeLocale,
        reloadLocales,
        loading,
        error,
      }}
    >
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={{
          ...DateFnsLocales[locale],
          options: {
            weekStartsOn: GLOBAL_DATE_CONFIG.WEEK_STARTS_ON
          },
        }}
      >
        {debouncedLoading ? <Spinner /> : children}
      </LocalizationProvider>
    </LocaleContext.Provider>
  );
}
